import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ConsoleUserService} from "@core-services/console-user.service";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {PageOffering} from "@core/model/side-nav.model";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {RegisterUserRequest} from "@ui-kit/framework/security/model/user.model";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {lastValueFrom} from "rxjs";
import {RetailerViewComponent} from "../../retailer-view/retailer-view.component";
import {RegisterRetailer, Retailer, TenantType} from "../../retailers.model";
import {CreateARetailerService} from "./create-a-retailer.service";
import {CustomValidators} from '@ui-kit/framework/util/validators/custom-validators';

@Component({
  selector: 'app-create-a-retailer-dialog',
  templateUrl: './create-a-retailer-dialog.component.html',
})
export class CreateARetailerDialogComponent implements OnInit {
  offering!: PageOffering;
  retailerForm: FormGroup;
  userCreated = false;
  errMessage = '';
  userId: number | null = null;
  openRetailerInNewTab = false;

  constructor(public dialogRef: MatDialogRef<CreateARetailerDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              public loader: LoadingService,
              private userService: ConsoleUserService,
              private createRetailerService: CreateARetailerService) {
    this.offering = data.offering;
    this.openRetailerInNewTab = data.openRetailerInNewTab;


    this.retailerForm = new FormGroup({
      firstName: new FormControl(null, Validators.required),
      lastName: new FormControl(null, Validators.required),
      email: new FormControl(null, [Validators.required, Validators.email]),
      companyName: new FormControl(null, Validators.required),
      industryTypes: new FormControl([], Validators.required),
      websiteUrl: new FormControl(null, CustomValidators.websiteUrl.bind(this)),
      socialMediaUrl: new FormControl(null, CustomValidators.websiteUrl.bind(this)),
    })
  }

  ngOnInit() {
    this.disableUserFormControls();
    this.disableRetailerFormControls();
  }

  submitForm() {
    if (this.userCreated) {
      this.registerRetailer();
    } else {
      this.registerUser()
        .then(res => {
          if (res) this.registerRetailer();
        })
    }
  }

  registerUser() {
    const newUser = new RegisterUserRequest({
      firstname: this.retailerForm.get('firstName')?.value,
      lastname: this.retailerForm.get('lastName')?.value,
      email: this.retailerForm.get('email')?.value,
    })

    return lastValueFrom(this.userService.registerNewPortalUser(newUser))
      .then(res => {
        this.userCreated = true;
        this.userId = res.id!;
        return true;
      })
      .catch((err) => {
        this.userCreated = false;
        switch (err.status) {
          case 400:
            if ('ValidationException' === err.headers.get('error_code')) {
              this.errMessage = '' + err.headers.get('error_message');
            }
            break;
          default:
            this.errMessage = 'An unexpected error occurred, please try again';
        }
        console.log(err);
        return false;
      })
  }

  registerRetailer() {
    const newRetailer: RegisterRetailer = {
      companyName: this.retailerForm.get('companyName')?.value,
      userId: this.userId,
      industries: this.retailerForm.get('industryTypes')?.value,
      websiteUrl: this.retailerForm.get('websiteUrl')?.value,
      socialMediaUrl: this.retailerForm.get('socialMediaUrl')?.value,
    }

    lastValueFrom(this.createRetailerService.registerNewRetailer(newRetailer))
      .then(res => {
        if (this.openRetailerInNewTab) this.openRetailerViewTab(res.id!.toString(), res.companyName, res.tenantType);
        this.close(res);
      })
      .catch(err => {
        console.error('Error: ', err);
      })
  }

  close(retailer: Retailer | null) {
    this.dialogRef.close(retailer);
  }

  openRetailerViewTab(id: string, companyName: string, tenantType: TenantType) {
    let payload: PageEventInterface = {
      componentToRegister: RetailerViewComponent,
      pageName: 'Retailer ' + id,
      pageHeader: this.offering!.label,
      data: { id: id, companyName: companyName, tenantType: tenantType },
      id: this.utils.generateUuidWithPrefix('retailer'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leads',
      pageCode: 'retailer',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  getErrorMessage(formField: string) {
    if (this.retailerForm.get(formField)!.hasError('required')) return 'You must enter a value';
    if (formField === 'email' && this.retailerForm.get(formField)!.hasError('email')) return 'Not a valid email';
    if (formField === 'websiteUrl' && this.retailerForm.get(formField)!.hasError('pattern')) return 'Not a valid URL';
    if (formField === 'socialMediaUrl' && this.retailerForm.get(formField)!.hasError('pattern')) return 'Not a valid URL';
    return null;
  }

  checkIfUserRelationshipExists(): void {
    let emailValue = this.retailerForm.get('email')!.value;
    if (emailValue != null && emailValue.length > 0 && this.isValidEmail(emailValue)) {

      let req: RegisterUserRequest = {
        email: emailValue,
        firstname: '',
        lastname: '',
      }

      this.userService.getUserRetailerRelationship(req)
        .subscribe({
          next: value => {
            if (value) {
              if (value.userId != null && value.userId != 0 && value.retailerId != null && value.retailerId != 0) {
                this.errMessage = "User already assigned to retailer";
                this.userCreated = false;
                this.userId = value.userId;
                this.retailerForm.get('firstName')!.setValue(value.firstName);
                this.retailerForm.get('lastName')!.setValue(value.lastName);
                this.retailerForm.get('companyName')!.setValue(value.companyName);
                this.disableRetailerFormControls();
                this.disableUserFormControls();
                return;
              }
              if (value.userId != null && value.userId != 0) {
                this.userCreated = true;
                this.userId = value.userId;
                this.retailerForm.get('firstName')!.setValue(value.firstName);
                this.retailerForm.get('lastName')!.setValue(value.lastName);
                this.disableUserFormControls();
                this.enableRetailerFormControls();
                return;
              }

              this.userCreated = false;
              this.userId = null;
              this.resetUserForm();
              this.enableUserFormControls();
              this.enableRetailerFormControls();
            }
            else {
              this.userCreated = false;
              this.userId = null;
              this.enableUserFormControls();
              this.enableRetailerFormControls();
            }
          }
        });
    } else {
      this.userCreated = false;
      this.userId = null;
      this.enableUserFormControls();
      this.enableRetailerFormControls();
      this.resetUserForm();
      this.errMessage = '';
    }
  }

  isValidEmail(email: string): boolean {
    const emailValidator = Validators.email({value: email} as any);
    return !emailValidator; // If it's `null`, it means the email is valid
  }

  disableUserFormControls() {
    this.retailerForm.get('firstName')?.disable();
    this.retailerForm.get('lastName')?.disable();
  }

  disableRetailerFormControls() {
    this.retailerForm.get('companyName')!.disable();
    this.retailerForm.get('industryTypes')!.disable();
    this.retailerForm.get('websiteUrl')!.disable();
    this.retailerForm.get('socialMediaUrl')!.disable();
  }

  enableUserFormControls() {
    this.retailerForm.get('email')!.enable();
    this.retailerForm.get('firstName')!.enable();
    this.retailerForm.get('lastName')!.enable();
  }

  enableRetailerFormControls() {
    this.retailerForm.get('companyName')!.enable();
    this.retailerForm.get('industryTypes')!.enable();
    this.retailerForm.get('websiteUrl')!.enable();
    this.retailerForm.get('socialMediaUrl')!.enable();
  }

  resetUserForm() {
    this.retailerForm.get('firstName')!.reset();
    this.retailerForm.get('lastName')!.reset();
  }

  resetRetailerForm() {
    this.retailerForm.get('companyName')!.reset();
    this.retailerForm.get('industryTypes')!.reset();
    this.retailerForm.get('websiteUrl')!.reset();
    this.retailerForm.get('socialMediaUrl')!.reset();
  }

  resetForm() {
    this.userCreated = false;
    this.userId = null;
    this.retailerForm.get('email')!.reset();
    this.disableUserFormControls();
    this.disableRetailerFormControls();
    this.resetUserForm();
    this.resetRetailerForm();
    this.errMessage = '';
  }

  get canSubmit(): boolean{
    return this.retailerForm.valid;
  }
}
